import { Pencil } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Card, Col, Row, Table } from 'react-bootstrap'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ModalUpdateSector from '../../../components/modal/sector/ModalUpdateSector'
import api from '../../../config/AxiosInstance'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  backgroundColor: '#f8f9fa',
  color: '#333333',
  fontWeight: '700',
  borderBottom: '1px solid #e9ecef',
  textTransform: 'uppercase',
  fontSize: '0.85rem'
}

const MasterSector = () => {
  const [showModalUpdate, setShowModalUpdate] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [sectors, setSectors] = useState([])
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const [editId, setEditId] = useState(0)
  const { per_page, current_page } = meta
  const abortControllerRef = useRef(null)

  async function fetchSectors() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setIsLoading(true)
    try {
      const res = await api.get('master/warehouses', {
        signal,
        params: { per_page, page: current_page }
      })
      const { metadata, data } = res.data

      setSectors(data)
      setMeta(metadata)
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchSectors()
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [per_page, current_page])

  function handleOnChangeMeta(e) {
    const perPageValue = parseInt(e.target.value, 10)
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: perPageValue,
      current_page: 1
    }))
    window.scrollTo(0, 0)
  }

  const handlePageChange = (data) => {
    const selectedPage = data.selected + 1
    setMeta((prevMeta) => ({ ...prevMeta, current_page: selectedPage }))
    window.scrollTo(0, 0)
  }

  function handleEdit(id) {
    setShowModalUpdate(true)
    setEditId(id)
  }

  return (
    <div className="container-fluid px-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Sector</h3>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12}>
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
            <Table responsive hover className="mb-0 b-table-frameless">
              <thead style={tableHeaderStyle}>
                <tr>
                  <th className="p-2 align-middle">Code</th>
                  <th className="p-2 align-middle">Name</th>
                  <th className="p-2 align-middle">City</th>
                  <th className="p-2 align-middle">Type</th>
                  <th className="p-2 align-middle">Customer</th>
                  <th className="p-2 align-middle text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={6} className="text-center p-5">
                      <Loader />
                    </td>
                  </tr>
                )}
                {!isLoading && sectors.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <RowEmpty colSpan={6} />
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  sectors?.map((sector) => (
                    <TbodyRow
                      data={sector}
                      key={sector.id}
                      onHandleEdit={() => handleEdit(sector.id)}
                    />
                  ))}
              </tbody>
            </Table>
            {sectors.length > 0 && (
              <div className="p-3 border-top bg-white">
                <TablePagination
                  meta={meta}
                  handlePageChange={handlePageChange}
                  handleOnChangeMeta={handleOnChangeMeta}
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <ModalUpdateSector
        show={showModalUpdate}
        onHide={() => {
          setShowModalUpdate(false)
        }}
        id={editId}
        refreshData={() => fetchSectors()}
      />
    </div>
  )
}
function TbodyRow({ data, onHandleEdit }) {
  return (
    <tr key={data.id}>
      <td className="p-2 align-middle fw-medium text-dark">
        {data.warehouse_code ?? '-'}
      </td>
      <td className="p-2 align-middle">{data.warehouse_name ?? '-'}</td>
      <td className="p-2 align-middle text-muted">
        {data.location?.area_name ?? '-'}
      </td>
      <td className="p-2 align-middle text-muted">
        {data.warehouse_type ?? '-'}
      </td>
      <td className="p-2 align-middle text-muted">
        {data.warehouse_customer?.warehouse_name ?? '-'}
      </td>
      <td className="p-2 align-middle text-end">
        <TableAction>
          <button
            className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0"
            style={{ width: '32px', height: '32px' }}
            onClick={() => onHandleEdit()}
          >
            <Pencil size={13} />
          </button>
        </TableAction>
      </td>
    </tr>
  )
}
export default MasterSector
